import { GridRow } from './GridRow';
import { dataSource } from './DataSources';
import { Debugger } from './Debugger';
import Maybe from '@Common/Core/Maybe';
import { MulticastDelegate } from '@Common/Core/MulticastDelegate';
import { Grid } from './SimpleGridLight';
import { GridColumn } from './GridColumn';
import { classNames, SelectionMode } from './Settings';
import { SortManager } from './SortManager';
import { GridRender } from './GridRender';
import { GridCell } from './GridCell';
import { FilterManager } from './FilterManager';
import { CompressedIndex, IndexUtils } from './IndexUtils';
import { comparers } from '@Common/Helpers/Comparers';
import { enumerable } from '@Common/Core/CoreDecorator';

type rowFilter = (r: GridRow) => boolean;

export class RowCollection<TData = any, TGridRow extends GridRow<TData> = GridRow<TData>> {
    public readonly onAfterAddRow = MulticastDelegate.create<ICallback<GridRow>>();
    private innerDataSource: dataSource.IDataSource;
    private rows: TGridRow[];
    private sourceIsRaw: boolean;
    private maxVisibleRowCount: number;
    private rowsForRemove: JQuery[] = [];
    private rowsDataForRemove: any[] = [];
    private lastSelected: TGridRow;
    private previewRow: TGridRow;
    private rowSelectedChangedHandler: (newSelectedRow: TGridRow) => void = $.noop;
    private onCellChangedHandler: (cell: GridCell) => void = $.noop;
    private onChangedHandler: () => void = $.noop;
    private isInLoadModeCount = 0;
    private onAfterDeleteRowHandler: (data?: any[]) => void = $.noop;

    constructor(
        public readonly grid: Grid,
        private readonly batchSize: number,
        private currentPage: number,
        private readonly pageSize: number,
        private readonly filterManager: FilterManager,
        private readonly sortManager: SortManager,
        private readonly gridRender: GridRender,
        public readonly selectionMode: SelectionMode = SelectionMode.select
    ) {
        this.maxVisibleRowCount = batchSize;
        this.filterManager = filterManager;
        this.sortManager = sortManager;
        this.gridRender = gridRender;
        this.grid = grid;

        this.rows = [];
    }

    @enumerable
    public get count(): number {
        return this.rows.length;
    }

    @enumerable
    public get isInLoadMode(): boolean {
        return this.isInLoadModeCount > 0;
    }

    public static isSelectedRow(gridRow: GridRow): boolean {
        return gridRow.tableRow().hasClass(classNames.selectedRow);
    }

    private static demarkSelectedRow(gridRow: GridRow): void {
        gridRow.tableRow().removeClass(classNames.selectedRow);
    }

    public findRows<TFindData = TData>(fn: (data: TFindData, row: TGridRow) => boolean): TGridRow[] {
        return this.rows.filter(r => fn((r.getData() as unknown) as TFindData, r));
    }

    public dataSource(data?: any): any {
        if (data === undefined) {
            return this.sourceIsRaw ? (this.innerDataSource.getSource() as any) : this.innerDataSource;
        } else {
            let source: dataSource.IDataSource;
            if (data === null) {
                source = new dataSource.EmptyDataSource();
            } else if (data instanceof Array) {
                this.sourceIsRaw = true;
                source = dataSource.CreateFromObjectArray(data);
            } else if (dataSource.isDataSource(data)) {
                this.sourceIsRaw = false;
                source = data;
            } else {
                throw new Error(`unsupported param data '${data}'`);
            }
            this.innerDataSource = source;
            this.rows = [];
            this.rows = this.createRows(0, source.getRowCount());
            this.updateDataAfterChange();
            return data;
        }
    }

    public getOrderedRows(): TGridRow[] {
        return this.sortManager.getIndex() as TGridRow[];
    }

    public getVisibilityRows(): TGridRow[] {
        return this.getOrderedRows().filter(row => row.isVisible());
    }

    public append(data: any[]): TGridRow[] {
        const newIndex = this.innerDataSource.getRowCount();
        this.innerDataSource.append(data);
        const newRows = this.createRows(newIndex, data.length);
        this.rows = this.rows.concat(newRows);

        // TODO manage isLoadMode and events
        for (const row of newRows) {
            this.onAfterAddRow(row);
        }

        if (!this.isInLoadMode) {
            this.updateDataAfterChange();
        }
        return newRows;
    }

    public ToArray(): TGridRow[] {
        return this.rows;
    }

    public isRenderedRow(row: TGridRow): boolean {
        return this.gridRender.isRenderedRow(row);
    }

    public attachRows(refreshData = false): void {
        this.detachRows();
        let vrc = this.getMaxVisibleRowCount();
        const gridInDom = this.gridRender.isVisible();

        let iterator = _(this.getVisibilityRows());
        if (this.pageSize) {
            iterator = iterator.slice(this.currentPage * this.pageSize).take(this.pageSize);
        }
        iterator.forEach(gridRow => {
            if (--vrc >= 0 || (gridInDom && !this.gridRender.bodyIsFilled())) {
                this.gridRender.attachRow(gridRow);
                gridRow.refresh(!refreshData);
            } else {
                return false;
            }
        });
    }

    public attachNextBatch(): boolean {
        const visibilityRows = this.getVisibilityRows();
        if (this.maxVisibleRowCount <= visibilityRows.length) {
            let prev = this.maxVisibleRowCount;
            this.maxVisibleRowCount = Math.min(this.maxVisibleRowCount + this.batchSize, visibilityRows.length);
            for (; prev < this.maxVisibleRowCount; prev++) {
                const r = visibilityRows[prev];
                this.gridRender.attachRow(r);
                r.refresh(true);
            }
        }
        return this.maxVisibleRowCount <= visibilityRows.length;
    }

    public deleteRows(arg: number | number[] | rowFilter): void {
        const removeRowIndexes = this.getIndexes(arg);
        const indexes: CompressedIndex[] = IndexUtils.compressAndRebuildIndex(removeRowIndexes);
        const rows = this.rows;

        for (const index of indexes) {
            const deletedRows = rows.splice(index.start, index.count);
            this.rowsForRemove.push(...deletedRows.map(r => r.tableRow()));
            this.rowsDataForRemove.push(...deletedRows.map(r => r.getData()));
            this.innerDataSource.remove(index.start, index.count);
        }
        for (let i = _.first(indexes).start; i < rows.length; i++) {
            rows[i].setBindIndex(i);
        }

        if (this.isInLoadModeCount === 0) {
            this.updateDataAfterChange();
        }
    }

    public getSelectedRow(): TGridRow {
        if (this.lastSelected && this.lastSelected.tableRow().parents().length > 0) {
            return this.lastSelected;
        }
        return null;
    }

    public setSelectedRow(gridRow: TGridRow, val: boolean): boolean {
        if (val) {
            if (this.lastSelected !== gridRow && this.lastSelected != null) {
                RowCollection.demarkSelectedRow(this.lastSelected);
            }
            this.lastSelected = gridRow;
            this.markSelectedRow(gridRow);
            this.rowSelectedChangedHandler(gridRow);
            return true;
        } else {
            if (this.lastSelected === gridRow) {
                this.lastSelected = null;
            } else {
                Debugger.writeLn('this.lastSelected <> gridRow');
            }

            RowCollection.demarkSelectedRow(gridRow);
            // TODO need remove this condition after release 6.1
            if (this.selectionMode === SelectionMode.deselect) {
                this.rowSelectedChangedHandler(null);
            }
            return false;
        }
    }

    public exitFromPreviewRow(): void {
        if (this.previewRow != null) {
            this.previewRow.cells().forEach(cell => cell.exitPreview());
        }
        this.previewRow = null;
    }

    public onRowSelectedChanged(fn: (newSelectedRow: TGridRow) => void): void {
        this.rowSelectedChangedHandler = fn;
    }

    public onCellChanged(fn: (cell: GridCell) => void): void {
        this.onCellChangedHandler = fn;
    }

    public getRowVisibility(row: TGridRow): boolean {
        return this.filterManager.getRowVisibility(row);
    }

    public setRowVisibility(gridRow: TGridRow, value: boolean): void {
        //this.filterManager.setRowVisibility(gridRow.dataIndex(), value);
    }

    public getOrderedIndex(gridRow: TGridRow): number {
        return this.sortManager.getOrderedIndex(gridRow.dataIndex());
    }

    public getValue(row: TGridRow, column: GridColumn): any {
        const accessor = column.getAccessor();
        if (accessor != null && accessor.get) {
            return accessor.get(row);
        }
        if (column.dataIndex() == null) {
            return undefined;
        }
        return this.innerDataSource.getValue(row.dataIndex(), column.dataIndex());
    }

    public setValue(dataIndex: TGridRow, index: GridColumn, value: any): void {
        const accessor = index.getAccessor();
        if (accessor != null && accessor.set) {
            accessor.set(dataIndex, value);
        } else if (dataIndex.dataIndex() != null) {
            this.innerDataSource.setValue(dataIndex.dataIndex(), index.dataIndex(), value);
        }
        this.rowDataChanged(dataIndex, index);
        const gridCell = dataIndex.getCell(index);
        gridCell.getColumn();
        this.onCellChangedHandler(gridCell);
        this.onChangedHandler();
    }

    public getData(dataIndex: TGridRow): any {
        return this.innerDataSource.getSource()[dataIndex.dataIndex()];
    }

    public setData(dataIndex: TGridRow, data: any): void {
        this.innerDataSource.getSource()[dataIndex.dataIndex()] = data;
        this.rowDataChanged(dataIndex);
    }

    public onChanged(fn: () => void): void {
        this.onChangedHandler = fn;
    }

    public beginLoad(): void {
        this.isInLoadModeCount++;
    }

    public endLoad(): void {
        this.isInLoadModeCount = Math.max(0, this.isInLoadModeCount - 1);
        if (!this.isInLoadMode) {
            this.updateDataAfterChange();
        }
    }

    public onAfterDeleteRow(fn: (data?: any[]) => void): void {
        this.onAfterDeleteRowHandler = fn;
    }

    public setCurrentPage(page: number): void {
        this.maxVisibleRowCount = this.batchSize;
        this.currentPage = page;
        this.attachRows();
    }

    public moveRow(oldIndex: number, newIndex: number): void {
        this.innerDataSource.move(oldIndex, newIndex);
        this.rows.move(oldIndex, newIndex);
        const vector = newIndex - oldIndex,
            dist = Math.abs(vector),
            dir = vector / dist;
        for (let i = 0; i <= dist; i++) {
            const newIndexInSource = oldIndex + dir * i;
            const row: TGridRow = this.rows[newIndexInSource];
            row.setBindIndex(newIndexInSource);
        }
        this.sortManager.resort();
    }

    public findNextVisibilityRow(startRow: TGridRow, upToDown = true): TGridRow {
        const orderedRows = this.getVisibilityRows(),
            index = orderedRows.indexOf(startRow);
        const newIndex = Math.abs((index + (upToDown ? 1 : -1)) % orderedRows.length);
        return orderedRows[newIndex];
    }

    public ensureVisible(gridRow: TGridRow, { animate } = { animate: false }): void {
        const wasRendered = gridRow.isRendered;
        while (!gridRow.isRendered && this.attachNextBatch()) {}

        const makeVisible = () => this.grid.gridLayout.bodyScroller.ensureVisible(gridRow.tableRow(), 0, 1, animate ? 500 : 0);
        if (!wasRendered) {
            this.grid.gridLayout.bodyScroller.refresh(makeVisible);
        } else {
            makeVisible();
        }
    }

    public selectNextRow({ up } = { up: false }): void {
        const vr = this.getVisibilityRows();
        if (vr.length === 0) {
            return;
        }
        const nextRowIndex = vr.indexOf(this.lastSelected) + (up ? -1 : 1);
        if (0 <= nextRowIndex && nextRowIndex < vr.length) {
            const nextRow = vr[nextRowIndex];
            this.setSelectedRow(nextRow, true);
            this.ensureVisible(nextRow, { animate: false });
        }
    }

    private createRows(startIndex: number, count: number): TGridRow[] {
        const res = [];
        const renderRow = this.gridRender.renderRow();
        for (let index = startIndex; index < startIndex + count; index++) {
            const gridRow = new GridRow(index, $(renderRow.cloneNode(true) as any), this);
            res.push(gridRow);
        }
        return res;
    }

    private resetPrevRow(): void {
        const isRenderedPreview = Maybe(this.previewRow)
            .read(prevRow => prevRow.isRendered)
            .valueOrDefault(false);
        if (!isRenderedPreview) {
            this.previewRow = null;
        }
    }

    private clearRemovedRows(): number {
        for (const row of this.rowsForRemove) {
            row.remove();
        }
        if (this.rowsForRemove.length) {
            this.onAfterDeleteRowHandler(this.rowsDataForRemove);
        }
        const deletedRowCount = this.rowsForRemove.length;
        this.rowsForRemove = [];
        this.rowsDataForRemove = [];
        return deletedRowCount;
    }

    private updateDataAfterChange(): void {
        this.clearRemovedRows();
        this.sortManager.resort();
        this.filterManager.refreshFilter(true);
        this.refreshEditorsForAll();
        this.grid.refresh();
        this.resetPrevRow();
        this.onChangedHandler();
    }

    private detachRows(): void {
        // ???? incorect function resolve;
        this.gridRender.detachRows();
    }

    private getMaxVisibleRowCount(): number {
        return Math.min(this.rows.length, this.maxVisibleRowCount);
    }

    private getIndexes(arg: number | number[] | rowFilter): number[] {
        let indexes: number[] = [];
        if (Function.isFunction<rowFilter>(arg)) {
            indexes = this.rows.filter(arg).map(r => r.dataIndex());
        } else if (Number.isNumber(arg)) {
            indexes.push(arg);
        } else if (Array.isArray(arg)) {
            indexes = indexes.concat(arg);
        }
        indexes.sort(comparers.comparer);
        indexes.push(this.rows.length);
        return indexes;
    }

    private markSelectedRow(gridRow: TGridRow): void {
        gridRow.tableRow().addClass(classNames.selectedRow);
        this.makePreviewRow(gridRow);
    }

    private makePreviewRow(gridRow: TGridRow): void {
        if (this.previewRow != gridRow) {
            this.exitFromPreviewRow();
            gridRow.cells().forEach(cell => cell.preview());
            this.previewRow = gridRow;
        }
    }

    private refreshEditorsForAll(): void {
        for (const column of this.grid.columns()) {
            column.editor().refreshAll(this);
        }
    }

    private rowDataChanged(gridRow: TGridRow, index?: GridColumn): void {
        gridRow.refresh();
        if (!this.isInLoadModeCount) {
            this.sortManager.changeData(gridRow, index);
            this.filterManager.changeData(gridRow, index);
            const editor = index && index.editor();
            if (editor) {
                editor.refreshAll(this);
            }
            this.attachRows();
        }
    }
}
